import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import BackgroundIcons from '../components/BackgroundIcons';
import NotificationModal from '../components/NotificationModal';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    wedding_date: '',
    partner_name: '',
    venue_location: ''
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await apiService.users.getProfile();
      if (response.data.success) {
        const userData = response.data.user;
        setUser(userData);
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          wedding_date: userData.wedding_date ? userData.wedding_date.split('T')[0] : '',
          partner_name: userData.partner_name || '',
          venue_location: userData.venue_location || ''
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      // If API fails, try to get user from localStorage or token
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          setUser({ name: payload.name, email: payload.email });
          setFormData({
            name: payload.name || '',
            email: payload.email || '',
            wedding_date: '',
            partner_name: '',
            venue_location: ''
          });
        } catch (tokenError) {
          console.error('Error parsing token:', tokenError);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSave = async () => {
    try {
      const response = await apiService.users.updateProfile(formData);
      if (response.data.success) {
        setUser(response.data.user);
        setEditing(false);
        setNotification({
          show: true,
          message: 'Profile updated successfully! ✨',
          type: 'success'
        });
        
        // Trigger navbar refresh
        window.dispatchEvent(new CustomEvent('profileUpdated'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setNotification({
        show: true,
        message: 'Failed to update profile. Please try again.',
        type: 'error'
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  const profileIcons = [
    { emoji: '💍', size: '4xl', opacity: '15' },
    { emoji: '🥂', size: '4xl', opacity: '15' },
    { emoji: '💖', size: '4xl', opacity: '15' },
    { emoji: '✨', size: '4xl', opacity: '15' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-pink-25 to-champagne-50 relative overflow-hidden">
      <BackgroundIcons icons={profileIcons} />
      
      {/* Floating sparkles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse opacity-10"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 2}s`
            }}
          >
            <span className="text-rose-400 text-lg">✨</span>
          </div>
        ))}
      </div>
      
      <div className="container mx-auto px-4 py-8 relative z-10">
        
        {/* Magical Header */}
        <div className="text-center mb-16 relative">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-r from-rose-400 via-pink-400 to-gold-400 rounded-full shadow-2xl mb-8 animate-bounce">
            <span className="text-4xl animate-pulse">👤</span>
          </div>
          
          <h1 className="text-6xl md:text-7xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-6 tracking-wide">
            Your Magical Profile
          </h1>
          
          <div className="flex items-center justify-center mb-6">
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
            <div className="mx-4 text-3xl animate-bounce">✨</div>
            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-gold-400 to-transparent rounded-full"></div>
          </div>
          
          <p className="text-2xl text-gray-700 font-light italic">
            Manage your enchanted wedding planning journey 💖
          </p>
        </div>

        {/* Magical Profile Card */}
        <div className="relative mb-16">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 to-rose-50/80 rounded-3xl blur-sm"></div>
          <div className="relative bg-gradient-to-br from-white/90 to-rose-25/90 rounded-3xl shadow-2xl border border-white/50 backdrop-blur-sm p-8 md:p-12 max-w-4xl mx-auto overflow-hidden">
            
            {/* Magical sparkles */}
            <div className="absolute top-4 right-4 flex gap-1 opacity-60">
              <div className="w-2 h-2 bg-rose-400 rounded-full animate-ping" style={{animationDelay: '0s'}}></div>
              <div className="w-2 h-2 bg-pink-400 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
              <div className="w-2 h-2 bg-gold-400 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
            </div>
            
            {/* Profile Header */}
            <div className="text-center mb-12">
              <div className="relative mb-8">
                <div className="bg-gradient-to-r from-rose-400 via-pink-500 to-purple-500 rounded-full w-32 h-32 flex items-center justify-center mx-auto shadow-2xl transform hover:scale-110 transition-all duration-300">
                  <span className="text-6xl animate-bounce">👰</span>
                </div>
                {/* Magical glow */}
                <div className="absolute inset-0 w-32 h-32 rounded-full bg-gradient-to-r from-rose-400 to-purple-500 opacity-30 blur-xl mx-auto animate-pulse"></div>
              </div>
              <h2 className="text-4xl font-serif font-bold bg-gradient-to-r from-rose-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-3">
                {user?.name || 'Beautiful Bride-to-be'}
              </h2>
              <p className="text-xl text-gray-600 font-light">{user?.email}</p>
            </div>

          {/* Magical Profile Form */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Name */}
            <div className="group">
              <label className="block text-lg font-serif font-semibold text-gray-800 mb-3 flex items-center">
                <span className="mr-2 text-2xl">✨</span>
                Full Name
              </label>
              {editing ? (
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-6 py-4 border-2 border-rose-200 rounded-2xl focus:ring-4 focus:ring-rose-200 focus:border-rose-400 transition-all duration-300 bg-gradient-to-r from-white to-rose-50 text-lg font-medium"
                />
              ) : (
                <div className="px-6 py-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border border-rose-200 text-gray-800 text-lg font-medium shadow-inner">
                  {user?.name || 'Not set'}
                </div>
              )}
            </div>

            {/* Email */}
            <div className="group">
              <label className="block text-lg font-serif font-semibold text-gray-800 mb-3 flex items-center">
                <span className="mr-2 text-2xl">💌</span>
                Email Address
              </label>
              <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200 text-gray-800 text-lg font-medium shadow-inner">
                {user?.email}
              </div>
              <p className="text-sm text-gray-500 mt-2 italic">✨ Email cannot be changed</p>
            </div>

            {/* Wedding Date */}
            <div className="group">
              <label className="block text-lg font-serif font-semibold text-gray-800 mb-3 flex items-center">
                <span className="mr-2 text-2xl">💍</span>
                Wedding Date
              </label>
              {editing ? (
                <input
                  type="date"
                  name="wedding_date"
                  value={formData.wedding_date}
                  onChange={handleInputChange}
                  className="w-full px-6 py-4 border-2 border-rose-200 rounded-2xl focus:ring-4 focus:ring-rose-200 focus:border-rose-400 transition-all duration-300 bg-gradient-to-r from-white to-rose-50 text-lg font-medium"
                />
              ) : (
                <div className="px-6 py-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border border-rose-200 text-gray-800 text-lg font-medium shadow-inner">
                  {user?.wedding_date ? user.wedding_date.split('T')[0] : 'Not set'}
                </div>
              )}
            </div>

            {/* Partner Name */}
            <div className="group">
              <label className="block text-lg font-serif font-semibold text-gray-800 mb-3 flex items-center">
                <span className="mr-2 text-2xl">💕</span>
                Partner's Name
              </label>
              {editing ? (
                <input
                  type="text"
                  name="partner_name"
                  value={formData.partner_name}
                  onChange={handleInputChange}
                  className="w-full px-6 py-4 border-2 border-rose-200 rounded-2xl focus:ring-4 focus:ring-rose-200 focus:border-rose-400 transition-all duration-300 bg-gradient-to-r from-white to-rose-50 text-lg font-medium"
                />
              ) : (
                <div className="px-6 py-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border border-rose-200 text-gray-800 text-lg font-medium shadow-inner">
                  {user?.partner_name || 'Not set'}
                </div>
              )}
            </div>

            {/* Venue Location - Full Width */}
            <div className="group md:col-span-2">
              <label className="block text-lg font-serif font-semibold text-gray-800 mb-3 flex items-center">
                <span className="mr-2 text-2xl">🏛️</span>
                Preferred Venue Location
              </label>
              {editing ? (
                <input
                  type="text"
                  name="venue_location"
                  value={formData.venue_location}
                  onChange={handleInputChange}
                  placeholder="e.g., New York, NY"
                  className="w-full px-6 py-4 border-2 border-rose-200 rounded-2xl focus:ring-4 focus:ring-rose-200 focus:border-rose-400 transition-all duration-300 bg-gradient-to-r from-white to-rose-50 text-lg font-medium"
                />
              ) : (
                <div className="px-6 py-4 bg-gradient-to-r from-rose-50 to-pink-50 rounded-2xl border border-rose-200 text-gray-800 text-lg font-medium shadow-inner">
                  {user?.venue_location || 'Not set'}
                </div>
              )}
            </div>

            {/* Magical Action Buttons */}
            <div className="md:col-span-2 flex flex-col sm:flex-row gap-6 pt-8">
              {editing ? (
                <>
                  <button
                    onClick={handleSave}
                    className="flex-1 relative overflow-hidden bg-gradient-to-r from-rose-400 via-pink-500 to-gold-400 text-white py-4 px-8 rounded-2xl font-bold text-lg animate-pulse hover:animate-none transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
                  >
                    <span className="relative z-10 flex items-center justify-center">
                      <span className="mr-3 text-2xl">💾</span>
                      Save Magical Changes
                    </span>
                    {/* Button shimmer */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>
                  </button>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setFormData({
                        name: user?.name || '',
                        email: user?.email || '',
                        wedding_date: user?.wedding_date ? user.wedding_date.split('T')[0] : '',
                        partner_name: user?.partner_name || '',
                        venue_location: user?.venue_location || ''
                      });
                    }}
                    className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white py-4 px-8 rounded-2xl font-bold text-lg hover:from-gray-500 hover:to-gray-600 transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
                  >
                    <span className="flex items-center justify-center">
                      <span className="mr-3 text-2xl">❌</span>
                      Cancel
                    </span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setEditing(true)}
                  className="w-full relative overflow-hidden bg-gradient-to-r from-rose-400 via-pink-500 to-gold-400 text-white py-4 px-8 rounded-2xl font-bold text-lg animate-pulse hover:animate-none transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-2xl"
                >
                  <span className="relative z-10 flex items-center justify-center">
                    <span className="mr-3 text-2xl">✏️</span>
                    Edit Magical Profile
                  </span>
                  {/* Button shimmer */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-700"></div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

        {/* Magical Quick Actions */}
        <div className="relative max-w-4xl mx-auto">
          <div className="absolute inset-0 bg-gradient-to-br from-white/80 to-purple-50/80 rounded-3xl blur-sm"></div>
          <div className="relative bg-gradient-to-br from-white/90 to-purple-25/90 rounded-3xl shadow-2xl border border-white/50 backdrop-blur-sm p-8 md:p-12 overflow-hidden">
            
            {/* Magical sparkles */}
            <div className="absolute top-4 right-4 flex gap-1 opacity-60">
              <div className="w-2 h-2 bg-purple-400 rounded-full animate-ping" style={{animationDelay: '0s'}}></div>
              <div className="w-2 h-2 bg-indigo-400 rounded-full animate-ping" style={{animationDelay: '0.5s'}}></div>
              <div className="w-2 h-2 bg-rose-400 rounded-full animate-ping" style={{animationDelay: '1s'}}></div>
            </div>
            
            <div className="text-center mb-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-2xl shadow-xl mb-6 transform hover:scale-110 transition-all duration-300">
                <span className="text-3xl animate-bounce">🚀</span>
              </div>
              <h3 className="text-4xl font-serif font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4">
                Magical Quick Actions
              </h3>
              <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-indigo-400 mx-auto rounded-full shadow-lg"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <a 
                href="/dashboard"
                className="group relative bg-gradient-to-br from-rose-50 to-pink-100 p-8 rounded-2xl border-2 border-rose-200/50 hover:border-rose-300 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl overflow-hidden"
              >
                <div className="absolute top-2 right-2 text-rose-300 opacity-50 group-hover:opacity-100 transition-opacity">✨</div>
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl flex items-center justify-center mr-6 shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <span className="text-3xl animate-bounce">📊</span>
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold text-gray-800 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:to-pink-600 transition-all duration-300">
                      View Dashboard
                    </h4>
                    <p className="text-gray-600 font-medium">Your wedding planning hub</p>
                  </div>
                </div>
              </a>
              
              <a 
                href="/vendor-vault/smart-vendor-matching"
                className="group relative bg-gradient-to-br from-purple-50 to-indigo-100 p-8 rounded-2xl border-2 border-purple-200/50 hover:border-purple-300 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-2xl overflow-hidden"
              >
                <div className="absolute top-2 right-2 text-purple-300 opacity-50 group-hover:opacity-100 transition-opacity">✨</div>
                <div className="flex items-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-2xl flex items-center justify-center mr-6 shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <span className="text-3xl animate-bounce">🔍</span>
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold text-gray-800 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-indigo-600 transition-all duration-300">
                      Find Vendors
                    </h4>
                    <p className="text-gray-600 font-medium">AI-powered vendor matching</p>
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* VowAI Notification Modal */}
      <NotificationModal
        show={notification.show}
        message={notification.message}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />
    </div>
  );
};

export default Profile;
